// ANGULAR
import { Component } from '@angular/core';

// BACKEND
import {
  FileParameter,
  GetReturnReceiptsOfTodayQuery,
  IReturnReceiptDto,
  IReturnReceiptPostDto,
  ReturnReceiptClient,
  ReturnReceiptDto,
  ReturnReceiptPostDto,
} from '@tech/api-clients/receipts';
import { PrettyConsole } from '@app/shared/utils';
import {
  IFilters,
  PermanentTablePaginatedComponent,
} from '../../components/permanent-table-paginated/permanent-table-paginated.component';
import { MatDialog } from '@angular/material/dialog';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, Observable } from 'rxjs';
import { PermanentTableComponent } from '@features/receipts/components/permanent-table/permanent-table.component';
import { ReceiptComponent } from '@features/receipts/components/receipt/receipt.component';
import { FileProcessorService } from '@app/shared/services/wasm/file-processor/file-processor.service';
import { NotificationService } from '@app/shared/services/notification/notification.service';
import { NotificationhubService } from '@app/shared/services/notificationhub/notificationhub.service';

import { NotificationsClient } from '@tech/api-clients/notification';
import { TranslateModule } from '@ngx-translate/core';

//~ Implementation

interface ReceiptDisplay extends IReturnReceiptPostDto {
  ok: boolean;
  [key: string]: string | number | boolean | Date | undefined;
}

@Component({
  selector: 'app-receipt-return',
  standalone: true,
  imports: [ReceiptComponent, TranslateModule],
  templateUrl: './receipt-return.component.html',
  styleUrl: './receipt-return.component.scss',
})
export class ReceiptReturnComponent extends ReceiptComponent {
  displayedColumns: string[] = [
    'file',
    'err_line',
    ...Object.keys(ReturnReceiptPostDto.prototype.toJSON()).map((c) =>
      c.toLowerCase()
    ),
  ];
  stagedReceipts: ReceiptDisplay[] = [];
  available_f: IFilters[] = [
    {
      label: 'Initial Date',
      type: 'date',
      id: 'initial_date',
    },
    { label: 'End Date', type: 'date', id: 'end_date' },
  ];
  validation_s = {
    'fecha recibo': {
      type: 'regex',
      pattern: '\\d{2}/\\d{2}/\\d{4}|\\d{4}/\\d{2}/\\d{2}',
    },
  };
  override wasm_split_pattern: string = ';';

  client_proc = (files: FileParameter[]) => {
    return this.child_client.uploadReturnFiles(files);
  };

  constructor(
    dialog: MatDialog,
    fileProcessor: FileProcessorService,
    notify: NotificationService,
    hub: NotificationhubService,
    private child_client: ReturnReceiptClient
  ) {
    super(dialog, fileProcessor, notify, hub);
    this.sse_name = 'CardReceipt';
    this.upload_handler = this.client_proc.bind(this);
    this.callme = this.callme_c.bind(this);
    this.get_paginated_data = this.get_paginated_data_c.bind(this);

    this.child_client.getFilterStructure(undefined).subscribe({
      next: (d) => {
        this.available_f = JSON.parse(d);
      },
      error: (e) => {
        PrettyConsole.ERROR(`Error while getting filter structure ${e}`);
      },
    });
  }

  callme_c = () => {
    this.dialog.closeAll();

    if (this.available_filters.length === 0) {
      this.child_client.getFilterStructure(undefined).subscribe({
        next: (d) => {
          this.available_filters = JSON.parse(d);
        },
        error: (e) => {
          PrettyConsole.ERROR(`Error while getting filter structure ${e}`);
        },
      });
    }
    const data_source = new PermanentReturnReceipts();
    this.dialog.open(PermanentTablePaginatedComponent<IReturnReceiptDto>, {
      width: '100vw',
      maxWidth: '98vw',
      height: '95vh',
      data: {
        receipts: data_source,
        display: Object.keys(ReturnReceiptDto.prototype.toJSON()).filter(
          (d) => d !== 'id'
        ),
        callback: this.get_paginated_data,
        filters: this.available_filters,
      },
    });
  };

  get_paginated_data_c = (
    page_index: number,
    page_size: number,
    filters: IFilters[]
  ) => {
    if (this.available_f.length === 0)
      this.child_client.getFilterStructure(undefined).subscribe({
        next: (d) => {
          this.available_f = JSON.parse(d);
        },
        error: (e) => {
          PrettyConsole.ERROR(`Error while getting filter structure ${e}`);
        },
      });
    let data = new BehaviorSubject<IReturnReceiptDto[]>([]);
    let count = new BehaviorSubject<number>(0);

    let filter_values: Record<string, any> = {};
    filters.forEach((f) => {
      filter_values[f.id] = f.value === null ? undefined : f.value;
    });

    this.child_client
      .getAllReturnReceipts(
        page_index,
        page_size,
        JSON.stringify(filter_values)
      )
      .subscribe({
        error: (e) => {
          PrettyConsole.ERROR(
            `Error while getting the return receipts of all time, page: ${page_index}, size: ${page_size}: ${e}`
          );
        },
        next: (d) => {
          if (d.count) count.next(d.count);
          if (d.receipts) data.next(d.receipts);
        },
      });
    return { count: count.asObservable(), data: data.asObservable() };
  };

  lookupPermanent_c = (_: Event): void => {
    const loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    const ds = new PermanentReturnReceipts();

    this.child_client
      .getReturnReceiptsOfToday(GetReturnReceiptsOfTodayQuery.fromJS({}))
      .subscribe({
        next: (receipts) => {
          ds.setData(receipts);
          loading$.next(false);
        },
        error: () => loading$.next(false),
      });

    this.dialog.open(PermanentTableComponent<PermanentReturnReceipts>, {
      width: '85vw',
      height: '80vh',
      data: {
        receipts: ds,
        display: Object.keys(ReturnReceiptDto.prototype.toJSON()),
        loading$,
        callback: this.callme,
      },
    });
  };
}

export class PermanentReturnReceipts extends DataSource<IReturnReceiptDto> {
  private data = new BehaviorSubject<IReturnReceiptDto[]>([]);

  connect(): Observable<IReturnReceiptDto[]> {
    return this.data.asObservable();
  }

  addRow(row: IReturnReceiptDto) {
    const current = this.data.getValue();
    this.data.next([...current, row]);
  }

  setData(rows: ReturnReceiptDto[]) {
    const data = rows.map((r) => r.toJSON());
    this.data.next(data);
  }

  disconnect(): void {}
}
